import styled from '@emotion/styled';
import { PlanBadge, tokens } from '@hotjar/hotsauce';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { ComponentProps, MouseEventHandler } from 'react';
import type { ReactionStyle } from '../../../../../domain/Surveys/Questions/SurveyQuestion';
import { ReactionIcon } from '../../../../ReactionIcon/ReactionIcon';

const ReactionFieldSet = styled.fieldset<{ twoLines: boolean }>`
  border: none;
  display: flex;
  flex-direction: row;
  gap: ${tokens.spacer.x2};
  padding: 0;

  ${({ twoLines }) => `
    ${
      twoLines &&
      `
      flex-direction: column;
      align-items: flex-start;
    `
    }
  `};
`;

const AdditionalOptions = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${tokens.spacer.x2};
`;

const ReactionInput = styled.input`
  appearance: none;
  position: absolute;
  background: transparent;
  margin: 0;
`;

const ReactionLabel = styled.label<{ isChecked: boolean }>`
  ${tokens.text('body2')};
  ${tokens.transition('background-color')};
  ${tokens.transition('border-color')};
  padding: ${tokens.spacer.x1};
  margin: 0;
  text-transform: none;
  background: ${tokens.color.ui_background};

  border: 1px solid ${tokens.BRAND_COLORS.neutral_400};
  border-radius: ${tokens.radius.m};

  white-space: nowrap;
  cursor: pointer;

  ${({ isChecked }) =>
    isChecked
      ? `
    border-color: ${tokens.color.ui_border_active};
    background-color: ${tokens.color.bg_active};
    `
      : `:hover {
      background-color: ${tokens.color.bg_hover};
    }`};

  &:focus-within {
    ${tokens.focus()};
  }
`;

const ReactionTitle = styled.span`
  display: inline-block;
`;

const IconAndTitle = styled.div`
  display: flex;
  gap: ${tokens.spacer.x1};
  align-items: center;

  svg {
    width: ${tokens.spacer.x2};
    height: ${tokens.spacer.x2};
  }
`;

const RadioIconButton = ({
  title,
  selectedStyle,
  reactionStyle,
  onStyleChange,
  onMouseEnterStyle,
  onMouseLeaveStyle,
}: {
  title: string;
  selectedStyle: ReactionStyle;
  reactionStyle: ReactionStyle;
  onStyleChange: (reactionStyle: ReactionStyle) => void;
  onMouseEnterStyle: (reactionStyle: ReactionStyle) => void;
  onMouseLeaveStyle: () => void;
}): JSX.Element => {
  const onChange = useCallback(() => onStyleChange(reactionStyle), [onStyleChange, reactionStyle]);
  const onMouseEnter = useCallback<MouseEventHandler<HTMLLabelElement>>(
    () => onMouseEnterStyle(reactionStyle),
    [onMouseEnterStyle, reactionStyle],
  );
  const onMouseLeave = useCallback<MouseEventHandler<HTMLLabelElement>>(
    () => onMouseLeaveStyle(),
    [onMouseLeaveStyle],
  );
  return (
    <ReactionLabel
      isChecked={reactionStyle === selectedStyle}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <ReactionInput
        type="radio"
        name="reaction_type"
        checked={reactionStyle === selectedStyle}
        onChange={onChange}
      />
      <IconAndTitle>
        <ReactionIcon reactionStyle={reactionStyle} />
        <ReactionTitle>{title}</ReactionTitle>
      </IconAndTitle>
    </ReactionLabel>
  );
};

export interface ReactionStyleSelectorProps {
  reactionStyle: ReactionStyle;
  onStyleChange: (reactionStyle: ReactionStyle) => void;
  onMouseEnterStyle: (reactionStyle: ReactionStyle) => void;
  onMouseLeaveStyle: () => void;
  showUpsell: () => void;
  hasAdditionalOptions: boolean;
  additionalOptionsPlan: ComponentProps<typeof PlanBadge>['kind'];
}

export const ReactionStyleSelector = ({
  reactionStyle,
  onStyleChange,
  onMouseEnterStyle,
  onMouseLeaveStyle,
  showUpsell,
  hasAdditionalOptions,
  additionalOptionsPlan,
}: ReactionStyleSelectorProps): JSX.Element => {
  const { t } = useTranslation('feedback');

  return (
    <ReactionFieldSet twoLines={!hasAdditionalOptions}>
      <RadioIconButton
        title={t('wizard.reaction-selector.buddies')}
        reactionStyle="default"
        selectedStyle={reactionStyle}
        onStyleChange={onStyleChange}
        onMouseEnterStyle={onMouseEnterStyle}
        onMouseLeaveStyle={onMouseLeaveStyle}
      />

      {!hasAdditionalOptions && additionalOptionsPlan && (
        <PlanBadge size="s" kind={additionalOptionsPlan} />
      )}

      <AdditionalOptions>
        <RadioIconButton
          title={t('wizard.reaction-selector.smileys')}
          reactionStyle="smiley"
          selectedStyle={reactionStyle}
          onStyleChange={hasAdditionalOptions ? onStyleChange : showUpsell}
          onMouseEnterStyle={onMouseEnterStyle}
          onMouseLeaveStyle={onMouseLeaveStyle}
        />
        <RadioIconButton
          title={t('wizard.reaction-selector.emojis')}
          reactionStyle="emoji"
          selectedStyle={reactionStyle}
          onStyleChange={hasAdditionalOptions ? onStyleChange : showUpsell}
          onMouseEnterStyle={onMouseEnterStyle}
          onMouseLeaveStyle={onMouseLeaveStyle}
        />

        <RadioIconButton
          title={t('wizard.reaction-selector.stars')}
          reactionStyle="star"
          selectedStyle={reactionStyle}
          onStyleChange={hasAdditionalOptions ? onStyleChange : showUpsell}
          onMouseEnterStyle={onMouseEnterStyle}
          onMouseLeaveStyle={onMouseLeaveStyle}
        />
      </AdditionalOptions>
    </ReactionFieldSet>
  );
};
